<template>
  <container-slot>
    <v-col cols="12">
      <h1 class="display-1 font-weight-medium">Add New Article</h1>
    </v-col>
    <v-col cols="12" v-show="successSubmit">
      <v-alert
        :type="!error ? `success` : `error`"
        ref="alert"
        dismissible
        @input="close"
        class="noMarg"
        v-show="successSubmit"
      >
        {{ msg }}
      </v-alert>
    </v-col>
    <v-col>
      <v-form ref="form" v-model="valid" @submit="submitForm">
        <v-col cols="12" md="6">
          <v-text-field
            prepend-icon="mdi-text-subject"
            name="title"
            label="Article Title"
            type="text"
            v-model="articleTitle"
            ref="userNameInput"
            :rules="articleTitleRules"
            required
          >
          </v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-file-input
            ref="inputImage"
            label="Article Header Image"
            prepend-icon="mdi-file-image"
            v-model="image"
            :rules="imageRules"
            @change="transformImage"
          >
          </v-file-input>
          <div style="max-width: 300px;" v-if="computedImage" class="mb-2">
            <img style="max-width: 300px;" :src="computedImage" />
          </div>
        </v-col>
        <v-col cols="12">
          <c-k-editor v-model="articleContent" />
        </v-col>
        <v-col cols="12">
          <v-btn
            @click="submitForm"
            color="primary"
            :disabled="!valid || !articleContent"
            :loading="loading"
            >Submit</v-btn
          >
        </v-col>
      </v-form>
    </v-col>
  </container-slot>
</template>

<script>
import CKEditorLoading from "@/components/CKEditorLoading";
import ContainerSlot from "@/components/ContainerSlot";
import LZString from "lz-string";
import axios from "axios";
import cheerio from "cheerio";

import { insertNews } from "@/utils/graphqlMutation";
import { news } from "@/utils/graphqlQuery";
import {
  SERVER_ENDPOINT,
  UPLOAD_IMAGE_ENDPOINT,
  CKEDITOR_DELETE_ENDPOINT,
  TRANSFORM_REQUEST,
} from "@/utils/constant";
import { lsUtils } from "@/utils/localStorageUtils";

export default {
  async beforeRouteLeave(to, from, next) {
    if (this.articleContent) {
      this.addImageNameToLocalStorage();
      const permission = confirm(this.permissionText);
      if (permission) {
        try {
          this.$emit("willExitPage", true);
          const {
            success,
            msg,
          } = await this.handleDeleteUploadedImagesCKEditor();
          if (!success) {
            throw new Error(msg);
          } else {
            this.$emit("willExitPage", false);
            next();
          }
        } catch (err) {
          this.$emit("willExitPage", false);
          this.error = true;
          this.msg = err.message;
          this.reset();
        }
      }
    } else {
      next();
    }
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.preventNavigation);
  },
  beforeMount() {
    window.addEventListener("beforeunload", this.preventNavigation);
  },
  components: {
    ContainerSlot,
    CKEditor: () => ({
      component: import("@/components/CKEditor"),
      loading: CKEditorLoading,
    }),
  },
  computed: {},
  data() {
    return {
      articleTitle: null,
      articleTitleRules: [(v) => v !== null || "Title can't empty"],
      articleContent: ``,
      computedImage: null,
      error: false,
      image: null,
      imageRules: [
        (v) =>
          !v ||
          typeof v !== "undefined" ||
          v.size < 200000 ||
          "Image size should be less than 200kb!",
        (v) =>
          !v ||
          typeof v !== "undefined" ||
          v.type.includes("image") ||
          "File must be an image",
      ],
      loading: false,
      msg: "",
      permissionText: "Form is not submitted, continue ?",
      successSubmit: false,
      valid: false,
    };
  },
  methods: {
    close() {
      this.successSubmit = false;
    },
    reset() {
      this.$refs.form.reset();
      this.articleContent = ``;
    },
    async xhrDeleteImageCKEditor(images) {
      const { data } = await axios.post(
        `${SERVER_ENDPOINT}${CKEDITOR_DELETE_ENDPOINT}`,
        {
          images,
        },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          transformRequest: (jsonData) => TRANSFORM_REQUEST(jsonData),
        }
      );
      const { status, msg } = data;
      if (status) {
        localStorage.removeItem("imagesInCKEditor");
      }
      return data;
    },
    preventNavigation(e) {
      if (!this.articleContent) {
        return;
      }
      this.addImageNameToLocalStorage();
      e.preventDefault();
      e.returnValue = "";
    },
    async submitForm(e) {
      e.preventDefault();
      this.successSubmit = false;
      let filename = "";
      if (this.valid) {
        this.loading = true;
        try {
          const { id: userId } = lsUtils.getItem("userToken").userData;
          if (this.image) {
            const formData = new FormData();
            formData.append("file_image", this.image);
            const { data: uploadResponse } = await axios.post(
              SERVER_ENDPOINT + UPLOAD_IMAGE_ENDPOINT,
              formData
            );
            if (!uploadResponse.success) {
              throw new Error("Error while uploading image");
            }
            filename = uploadResponse.imagePath;
          }
          try {
            const encodedArticleContent = LZString.compressToEncodedURIComponent(
              this.articleContent
            );
            const newsObj = {
              title: this.articleTitle,
              content: encodedArticleContent,
              isDeleted: 0,
              image: filename,
              authorId: userId,
            };
            const gqlNews = news;
            const { data } = await this.$apollo.mutate({
              mutation: insertNews,
              variables: {
                newsObj,
              },
              update: (store, { data: { insertNews: newsData } }) => {
                try {
                  const cachedNews = store.readQuery({ query: gqlNews });
                  cachedNews.news.push(newsData.data);
                  store.writeQuery({
                    query: gqlNews,
                    data: {
                      news: cachedNews,
                    },
                  });
                  return newsData.data;
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.log('No "news" cache found or ' + err.message);
                  return null;
                }
              },
            });
            const { success, msg } = data.insertNews;
            if (!success) {
              throw new Error(msg);
            }
            this.error = false;
            this.msg = msg;
            this.reset();
          } catch (error) {
            throw new Error(error);
          }
        } catch (error) {
          this.error = true;
          this.msg = error.message;
        } finally {
          this.successSubmit = true;
          this.loading = false;
        }
      }
    },
    transformImage() {
      if (this.image) {
        if (!this.image.type.includes("image")) return null;
        const reader = new FileReader();
        reader.readAsDataURL(this.image);
        reader.onload = () => {
          this.computedImage = reader.result;
        };
      }
      this.computedImage = null;
    },
    addImageNameToLocalStorage() {
      const that = this;
      const $ = cheerio.load(this.articleContent);
      const imagesUploadedInCKEditor = $("img");
      let images = localStorage.getItem("imagesInCKEditor")
        ? JSON.parse(localStorage.getItem("imagesInCKEditor"))
        : [];

      if (imagesUploadedInCKEditor.length) {
        imagesUploadedInCKEditor.each(function (i, elem) {
          const imgSrc = $(this).attr("src");
          const srcSpl = imgSrc.split("/");
          const len = srcSpl.length;
          const folderAndFileName = srcSpl[len - 2] + "/" + srcSpl[len - 1];
          if (!images.includes(folderAndFileName)) {
            images.push(folderAndFileName);
          }
        });
        localStorage.setItem("imagesInCKEditor", JSON.stringify(images));
      }
    },
    async handleDeleteUploadedImagesCKEditor() {
      const images = JSON.parse(localStorage.getItem("imagesInCKEditor"));
      if (images && images.length) {
        try {
          const { status, msg } = await this.xhrDeleteImageCKEditor(images);
          return {
            success: status,
            msg,
          };
        } catch (err) {
          return {
            success: false,
            msg: err.message,
          };
        }
      } else {
        return {
          success: true,
          msg: "No images need to be deleted",
        };
      }
    },
  },
  async mounted() {
    this.$emit("willExitPage", true);
    try {
      const { success, msg } = await this.handleDeleteUploadedImagesCKEditor();
      if (!success) {
        throw new Error(err.message);
      }
    } catch (err) {
      this.error = true;
      this.msg = err.message;
    }
    this.$emit("willExitPage", false);
  },
  name: "AddNews",
  title() {
    return "Add New News";
  },
};
</script>

<style scoped>
.noMarg {
  margin: 0;
}
</style>
